/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// Types
import {
	TApiResponseCreateProducer,
	TBodyCreateProducer,
	TBodyUpdateProducer,
} from '../types';
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const updateProducer = ({
	data,
	id,
}: {
	data: TBodyUpdateProducer;
	id: string;
}): Promise<TApiResponseCreateProducer> => {
	return apiClient.patch(`/producers/${id}`, data);
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseUpdateProducerOptions = {
	mutationConfig?: TMutationConfig<typeof updateProducer>;
	page: number;
	size: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useUpdateProducer = ({
	page,
	size,
	mutationConfig,
}: TUseUpdateProducerOptions) => {
	const { t } = useTranslation();
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: updateProducer,
		onSuccess: async (...args) => {
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t(
					'producers.notifications.producerSuccessfullyUpdated'
				),
				color: 'success',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.producers.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
